import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { bidsAPI, vacanciesAPI, bursariesAPI, projectsAPI, galleryAPI, blogsAPI } from '../services/api';
import { useAppSelector } from '../redux/hooks';
import "../styles/theme.css";

interface Stats {
  bids:      { total: number; open: number; closed: number; closingSoon: number };
  vacancies: { total: number; open: number; closed: number; closingSoon: number };
  bursaries: { total: number; open: number; closed: number; closingSoon: number };
  projects:  { total: number };
  gallery:   { total: number; images: number; videos: number };
  blogs:     { total: number; published: number; drafts: number };
}

/* ── helpers ── */
const isClosingSoon = (d: string) => {
  if (!d) return false;
  const days = Math.ceil((new Date(d).getTime() - Date.now()) / 86_400_000);
  return days <= 7 && days >= 0;
};
const isClosed = (d: string) => !!d && new Date(d) < new Date();

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/* ── stat card config ── */
interface CardCfg {
  key:    keyof Stats;
  label:  string;
  icon:   string;
  iconBg: string;
  to:     string;
  toLabel:string;
  badges: (s: Stats) => { label: string; cls: string }[];
}

const CARDS: CardCfg[] = [
  {
    key: 'bids', label: 'Bids', icon: '📋', iconBg: '#dbeeff', to: '/bids', toLabel: 'View all bids',
    badges: (s) => [
      { label: `${s.bids.open} Open`,   cls: 'cms-badge-success' },
      { label: `${s.bids.closed} Closed`, cls: 'cms-badge-danger' },
      ...(s.bids.closingSoon > 0 ? [{ label: `${s.bids.closingSoon} Closing soon`, cls: 'cms-badge-warning' }] : []),
    ],
  },
  {
    key: 'vacancies', label: 'Vacancies', icon: '💼', iconBg: '#dcfce7', to: '/vacancies', toLabel: 'View all vacancies',
    badges: (s) => [
      { label: `${s.vacancies.open} Open`,   cls: 'cms-badge-success' },
      { label: `${s.vacancies.closed} Closed`, cls: 'cms-badge-danger' },
      ...(s.vacancies.closingSoon > 0 ? [{ label: `${s.vacancies.closingSoon} Closing soon`, cls: 'cms-badge-warning' }] : []),
    ],
  },
  {
    key: 'bursaries', label: 'Bursaries', icon: '🎓', iconBg: '#fef9c3', to: '/bursaries', toLabel: 'View all bursaries',
    badges: (s) => [
      { label: `${s.bursaries.open} Open`,   cls: 'cms-badge-success' },
      { label: `${s.bursaries.closed} Closed`, cls: 'cms-badge-danger' },
      ...(s.bursaries.closingSoon > 0 ? [{ label: `${s.bursaries.closingSoon} Closing soon`, cls: 'cms-badge-warning' }] : []),
    ],
  },
  {
    key: 'blogs', label: 'Blog Posts', icon: '✍️', iconBg: '#f3e8ff', to: '/blogs', toLabel: 'View all blogs',
    badges: (s) => [
      { label: `${s.blogs.published} Published`, cls: 'cms-badge-success' },
      { label: `${s.blogs.drafts} Drafts`,       cls: 'cms-badge-draft' },
    ],
  },
  {
    key: 'projects', label: 'Projects', icon: '🚧', iconBg: '#ffedd5', to: '/projects', toLabel: 'View all projects',
    badges: () => [],
  },
  {
    key: 'gallery', label: 'Gallery', icon: '🖼️', iconBg: '#fce7f3', to: '/gallery', toLabel: 'View gallery',
    badges: (s) => [
      { label: `${s.gallery.images} Images`, cls: 'cms-badge-info' },
      { label: `${s.gallery.videos} Videos`, cls: 'cms-badge-brand' },
    ],
  },
];

const QUICK_ACTIONS = [
  { to: '/bids/new',       label: '+ New Bid' },
  { to: '/vacancies/new',  label: '+ New Vacancy' },
  { to: '/bursaries/new',  label: '+ New Bursary' },
  { to: '/blogs/new',      label: '+ New Blog Post' },
  { to: '/projects/new',   label: '+ New Project' },
  { to: '/events/new',     label: '+ New Event' },
  { to: '/gallery/new',    label: '+ Upload to Gallery' },
];

/* ── component ── */
const Dashboard: React.FC = () => {
  const reduxUser = useAppSelector((state: any) => state.auth?.user);
  const [user] = useState(() => {
    if (reduxUser) return reduxUser;
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });

  const [stats, setStats] = useState<Stats>({
    bids:      { total: 0, open: 0, closed: 0, closingSoon: 0 },
    vacancies: { total: 0, open: 0, closed: 0, closingSoon: 0 },
    bursaries: { total: 0, open: 0, closed: 0, closingSoon: 0 },
    projects:  { total: 0 },
    gallery:   { total: 0, images: 0, videos: 0 },
    blogs:     { total: 0, published: 0, drafts: 0 },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const [bidsRes, vacRes, burRes, projRes, galRes, blogRes] = await Promise.all([
        bidsAPI.getAll(), vacanciesAPI.getAll(), bursariesAPI.getAll(),
        projectsAPI.getAll(), galleryAPI.getAll(), blogsAPI.getAll(),
      ]);
      const bids      = bidsRes.data;
      const vacancies = vacRes.data;
      const bursaries = burRes.data;
      const gallery   = galRes.data;
      const blogs     = blogRes.data;

      setStats({
        bids:      { total: bids.length, open: bids.filter((b: any) => !isClosed(b.closing_date)).length, closed: bids.filter((b: any) => isClosed(b.closing_date)).length, closingSoon: bids.filter((b: any) => isClosingSoon(b.closing_date)).length },
        vacancies: { total: vacancies.length, open: vacancies.filter((v: any) => !isClosed(v.closing_date)).length, closed: vacancies.filter((v: any) => isClosed(v.closing_date)).length, closingSoon: vacancies.filter((v: any) => isClosingSoon(v.closing_date)).length },
        bursaries: { total: bursaries.length, open: bursaries.filter((b: any) => !isClosed(b.closing_date)).length, closed: bursaries.filter((b: any) => isClosed(b.closing_date)).length, closingSoon: bursaries.filter((b: any) => isClosingSoon(b.closing_date)).length },
        projects:  { total: projRes.data.length },
        gallery:   { total: gallery.length, images: gallery.filter((g: any) => g.type === 'image').length, videos: gallery.filter((g: any) => g.type === 'video').length },
        blogs:     { total: blogs.length, published: blogs.filter((b: any) => b.status === 'published').length, drafts: blogs.filter((b: any) => b.status === 'draft').length },
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'there';
  const totalAlerts = stats.bids.closingSoon + stats.vacancies.closingSoon + stats.bursaries.closingSoon;

  /* ── loading ── */
  if (loading) {
    return (
      <div className="cms-page">
        <Container>
          <div style={{ padding: '80px 0', textAlign: 'center' }}>
            <div style={{ marginBottom: 16 }}>
              <Spinner animation="border" style={{ color: 'var(--brand-700)', width: 36, height: 36, borderWidth: 3 }} />
            </div>
            <p className="t-small t-muted">Loading your dashboard…</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <Container>

        {/* ── Header ── */}
        <div className="cms-fade-in" style={{ marginBottom: 'var(--space-8)' }}>
          <div className="cms-flex-between" style={{ flexWrap: 'wrap', gap: 'var(--space-4)' }}>
            <div>
              <p className="t-caption t-muted" style={{ marginBottom: 4 }}>
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <h1 className="cms-page-title">
                {greeting()}, {fullName} 👋
              </h1>
              <p className="cms-page-subtitle" style={{ marginTop: 4 }}>
                Here's an overview of your content platform.
              </p>
            </div>

            {/* Alert pill */}
            {totalAlerts > 0 && (
              <div className="cms-alert cms-alert-warning cms-fade-in" style={{ maxWidth: 320, padding: '10px 16px' }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <span className="t-small">
                  <strong>{totalAlerts} item{totalAlerts > 1 ? 's' : ''}</strong> closing within 7 days — review soon.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <Row className="g-4" style={{ marginBottom: 'var(--space-8)' }}>
          {CARDS.map((card, i) => {
            const data = stats[card.key] as any;
            const badges = card.badges(stats);
            return (
              <Col key={card.key} xs={12} sm={6} lg={4}>
                <div
                  className={`cms-stat-card cms-fade-in cms-fade-in-${Math.min(i + 1, 6)}`}
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  {/* Top row */}
                  <div className="cms-flex-between" style={{ marginBottom: 'var(--space-4)' }}>
                    <div>
                      <p className="cms-stat-label">{card.label}</p>
                      <p className="cms-stat-number">{data.total}</p>
                    </div>
                    <div className="cms-stat-icon" style={{ background: card.iconBg }}>
                      {card.icon}
                    </div>
                  </div>

                  {/* Badges */}
                  {badges.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', flex: 1 }}>
                      {badges.map((b) => (
                        <span key={b.label} className={`cms-badge cms-badge-dot ${b.cls}`}>{b.label}</span>
                      ))}
                    </div>
                  )}

                  {badges.length === 0 && (
                    <p className="t-small t-muted" style={{ flex: 1, margin: '0 0 var(--space-4)' }}>
                      Active items in portfolio
                    </p>
                  )}

                  {/* Link */}
                  <Link to={card.to} className="t-small t-brand" style={{ fontWeight: 600, textDecoration: 'none' }}>
                    {card.toLabel} →
                  </Link>
                </div>
              </Col>
            );
          })}
        </Row>

        {/* ── Quick Actions ── */}
        <div
          className="cms-card cms-fade-in cms-fade-in-6"
          style={{ padding: 'var(--space-6)' }}
        >
          <div className="cms-flex-between" style={{ marginBottom: 'var(--space-5)', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <div>
              <h2 className="t-subhead" style={{ margin: 0 }}>Quick Actions</h2>
              <p className="t-small t-muted" style={{ margin: '2px 0 0' }}>Create new content instantly</p>
            </div>
            <span className="cms-badge cms-badge-brand">⚡ Shortcuts</span>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            {QUICK_ACTIONS.map((a) => (
              <Link key={a.to} to={a.to} className="cms-btn cms-btn-primary cms-btn-sm">
                {a.label}
              </Link>
            ))}
          </div>
        </div>

      </Container>
    </div>
  );
};

export default Dashboard;